import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const enquiryTypes = [
  'General',
  'Corporate and strategic',
  'Portfolio',
  'Property and land',
  'Intellectual property and licensing',
  'Media and speaking',
  'Stewardship',
  'Other',
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', organisation: '', email: '', phone: '', enquiryType: '', message: '', _honeypot: '' });
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form._honeypot) return;
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          organisation: form.organisation,
          email: form.email,
          phone: form.phone,
          enquiry_type: form.enquiryType,
          message: form.message,
        }),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', organisation: '', email: '', phone: '', enquiryType: '', message: '', _honeypot: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
    setSubmitting(false);
  };

  return (
    <Layout>
      <SEO
        title="Contact Sandend House"
        description="Contact Sandend House regarding relevant corporate, strategic, property, intellectual-property, media or stewardship enquiries."
        path="/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact Sandend House",
          "url": "https://theknox.co.uk/contact"
        }}
      />

      <section className="py-20 lg:py-28">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-4">Contact</p>
              <h1 className="font-serif text-4xl lg:text-5xl mb-4" data-testid="contact-heading">Contact Sandend House</h1>
              <p className="text-[var(--sh-grey)] mb-10">
                For relevant corporate, strategic, property, intellectual-property, media, speaking and stewardship enquiries.
              </p>

              {status === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded p-4 mb-8" data-testid="success-message">
                  <p className="text-green-800 text-sm font-medium">Thank you. Your enquiry has been received and we will respond in due course.</p>
                </div>
              )}

              {status === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded p-4 mb-8" data-testid="error-message">
                  <p className="text-red-800 text-sm font-medium">There was an error submitting your enquiry. Please try again or email us directly.</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                {/* Honeypot */}
                <div className="hidden">
                  <input type="text" value={form._honeypot} onChange={(e) => setForm({ ...form, _honeypot: e.target.value })} tabIndex={-1} autoComplete="off" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[var(--sh-charcoal)] mb-1.5">Name *</label>
                    <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-2.5 border border-[var(--sh-rule)] rounded-sm bg-white text-[var(--sh-charcoal)] focus:outline-none focus:border-[var(--sh-ink)]" data-testid="input-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--sh-charcoal)] mb-1.5">Organisation</label>
                    <input type="text" value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} className="w-full px-4 py-2.5 border border-[var(--sh-rule)] rounded-sm bg-white text-[var(--sh-charcoal)] focus:outline-none focus:border-[var(--sh-ink)]" data-testid="input-organisation" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[var(--sh-charcoal)] mb-1.5">Email address *</label>
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-2.5 border border-[var(--sh-rule)] rounded-sm bg-white text-[var(--sh-charcoal)] focus:outline-none focus:border-[var(--sh-ink)]" data-testid="input-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--sh-charcoal)] mb-1.5">Telephone number</label>
                    <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-2.5 border border-[var(--sh-rule)] rounded-sm bg-white text-[var(--sh-charcoal)] focus:outline-none focus:border-[var(--sh-ink)]" data-testid="input-phone" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--sh-charcoal)] mb-1.5">Enquiry type *</label>
                  <select required value={form.enquiryType} onChange={(e) => setForm({ ...form, enquiryType: e.target.value })} className="w-full px-4 py-2.5 border border-[var(--sh-rule)] rounded-sm bg-white text-[var(--sh-charcoal)] focus:outline-none focus:border-[var(--sh-ink)]" data-testid="input-enquiry-type">
                    <option value="">Select an enquiry type</option>
                    {enquiryTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--sh-charcoal)] mb-1.5">Message *</label>
                  <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-2.5 border border-[var(--sh-rule)] rounded-sm bg-white text-[var(--sh-charcoal)] focus:outline-none focus:border-[var(--sh-ink)] resize-y" data-testid="input-message" />
                </div>

                <div>
                  <button type="submit" disabled={submitting} className="btn-primary" data-testid="submit-btn">
                    {submitting ? 'Sending...' : 'Send Enquiry'}
                  </button>
                </div>

                <p className="text-[var(--sh-grey)] text-xs">
                  Your information will be used only to review and respond to your enquiry. See our <a href="/privacy" className="underline hover:text-[var(--sh-ink)]">Privacy Policy</a> for further information.
                </p>
              </form>
            </div>

            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <div className="bg-white border border-[var(--sh-rule)] rounded p-8">
                  <h2 className="font-serif text-xl mb-4">Direct contact</h2>
                  <a href="mailto:contact@theknox.co.uk" className="text-[var(--sh-ink)] text-sm font-medium hover:text-[var(--sh-brass)] transition-colors" data-testid="direct-email">
                    contact@theknox.co.uk
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
